import React, { useEffect, useState } from "react";
import ContactInfoForm from "../../../components/Forms/ContactInfoForm";
import LoyaltyContactInfoForm from "../../../components/Forms/LoyaltyContactInfoForm";
import { CustomModal } from "../../../components/Modal/CustomModal";
import { useAuthState } from "../../../context/UserAuthentication";
import { getUser, isGuestUser } from "../../../helpers/helperMethods";
import EditIcon from "../../../images/Edit.svg";
import { useIsDesignV2 } from "../../../hooks/useIsDesignV2";
import { FormikProps } from "formik";
import { ContactInfoFormFields } from "../../../models/forms.model";
interface IContactMethodProps {
  isOrdered?: boolean,
  orderDetails?: any;
  contactInfoMessage?: string,
  setContactInfoMessage?: (string) => void
  contactFormRef?: React.Ref<FormikProps<ContactInfoFormFields>>;
  /**
   * API-driven prefill for the guest checkout contact form. When provided it fully
   * drives the inline form's initial values (kept in local state only, never
   * localStorage/redux). Falls back to the stored user when not supplied.
   */
  guestContactPrefill?: any;
  /**
   * API-driven prefill for the loyalty checkout contact form (profile API).
   * When provided, used instead of getUser()/localStorage.
   */
  loyaltyContactPrefill?: {
    first_name?: string;
    last_name?: string;
    email?: string;
    phone?: string;
  };
  /** Saved customer email/phone — skip async validate when unchanged. */
  knownEmail?: string | null;
  knownPhone?: string | null;
}
const ContactMethod = (props: IContactMethodProps) => {
  const {
    isOrdered,
    contactInfoMessage,
    setContactInfoMessage,
    orderDetails,
    contactFormRef,
    guestContactPrefill,
    loyaltyContactPrefill,
    knownEmail,
    knownPhone,
  } = props
  const { authInfo } = useAuthState();
  const currentUser = getUser();
  const loyaltyUserInfo = loyaltyContactPrefill ?? currentUser;
  const { first_name, last_name, email, phone } = isGuestUser(authInfo)
    ? currentUser
    : loyaltyUserInfo;
  const isContactInfoComplete = first_name && last_name && email && phone;
  const [showContactForm, setShowContactForm] = useState(false);
  const handleShowContactForm = () => setShowContactForm(true);
  const handleCloseContactForm = () => setShowContactForm(false);
  const isDesignV2 = useIsDesignV2();
  const isGuest = isGuestUser(authInfo);

  useEffect(() => {
    if (isContactInfoComplete)
      setContactInfoMessage?.("");
  }, [currentUser])
  
  const formatPhoneNumber = (str) => {
    let cleaned = ('' + str).replace(/\D/g, '');
    let match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      return '(' + match[1] + ') ' + match[2] + '-' + match[3]
    };
    return null
  };

  // Guest checkout: inline ContactInfoForm (unchanged).
  if (isGuest && !isOrdered) {
    return (
      <>
        <h4 className={`text-start h-22 text-uppercase  ${isDesignV2 ? 'mb-2 f-s15 font-Visby-cf f-w6' :  'f-s20 font-Cls fw-normal f-sm-s16 mb-3'} ${isDesignV2 && isGuest ? 'mt-2' : ''}`}>
          Contact method
        </h4>
        {contactInfoMessage && (
          <span className="clr-dark-red f-s14 d-block text-start mb-2">{contactInfoMessage}</span>
        )}
        <ContactInfoForm
          inline
          formRef={contactFormRef}
          currentGuestUserInfo={
            guestContactPrefill !== undefined ? guestContactPrefill : currentUser
          }
          knownEmail={knownEmail}
          knownPhone={knownPhone}
        />
      </>
    );
  }

  // Loyalty checkout: same fields as the edit-contact modal, inline (guest-style UI).
  // Prefill + saveContactInfo / validation stay on LoyaltyContactInfoForm.
  if (!isGuest && !isOrdered) {
    return (
      <div className="mt-4">
        <h4 className={`text-start h-22 text-uppercase  ${isDesignV2 ? 'mb-2 f-s15 font-Visby-cf f-w6' :  'f-s20 font-Cls fw-normal f-sm-s16 mb-3'}`}>
          Contact method
        </h4>
        {contactInfoMessage && (
          <span className="clr-dark-red f-s14 d-block text-start mb-2">{contactInfoMessage}</span>
        )}
        <LoyaltyContactInfoForm
          inline
          formRef={contactFormRef}
          currentUserInfo={loyaltyUserInfo}
          knownEmail={knownEmail}
          knownPhone={knownPhone}
        />
      </div>
    );
  }

  // Ordered / thank-you (and any other ordered summary): existing read-only view.
  return (
    <>
      <div className="d-flex justify-content-between align-items-center">
        <h4 className={`text-start h-22 text-uppercase  ${isDesignV2 ? 'mb-0 f-s15 font-Visby-cf f-w6' :  'f-s20 font-Cls fw-normal f-sm-s16'}`}>
          Contact method
        </h4>
        {(authInfo.userId || currentUser.email || currentUser.first_name || currentUser.phone) &&
          <button className={`${isOrdered ? "d-none" : null} btn btn-custom  d-md-none f-s16 text_underline clr-text-grey ms-3 p-0 ${isDesignV2 ? 'edit-action-btn' : ''}`}
            onClick={handleShowContactForm}
            data-testid="contact-info-edit-button-on-checkout-for-mobile"
          >
           {!isDesignV2 && <img src={EditIcon} alt="edit"/>}
          </button>
        }
      </div>
      <div className={`d-flex justify-content-between align-items-center pb-md-0 ${isDesignV2 ? 'pb-2' : 'pb-4'}`}>
        <div>
          <span className={"clr-dark-red f-s14 d-flex"}>{contactInfoMessage}</span>
          <span className="d-block text-start font-Visby-cf f-s16 fw-bold  f-sm-s14 f-sm-w5" data-testid="customer-first-last-name-checkout">
            { isOrdered ? 
              `${orderDetails?.customer?.first_name} ${orderDetails?.customer?.last_name}` : 
              `${currentUser?.first_name} ${currentUser?.last_name}`
            }
          </span>
          <span className={`d-block text-start  ${isDesignV2 ? 'f-s14 f-w5 font-Visby-cf' : 'font-rale f-s16 f-w5 mb-2 f-sm-s14 f-sm-w5'}`} data-testid="customer-email-checkout">
            {isOrdered ? orderDetails?.customer?.email : currentUser?.email}
          </span>
          <span className={`d-block text-start f-s16 f-w5 mb-2 f-sm-s14 f-sm-w5 ${isDesignV2 ? 'font-Visby-cf'  : 'font-rale'}`} data-testid="customer-phone-checkout">
            {formatPhoneNumber(isOrdered ? orderDetails?.customer?.phone: currentUser?.phone)}
          </span>
        </div>
        <div className={isOrdered ? "d-none" : null}>
          {(authInfo.userId || currentUser.email || currentUser.first_name || currentUser.phone) &&
            <button className={`btn btn-custom f-s16 text_underline clr-text-grey ms-3 p-0 d-none d-md-block`}
              onClick={handleShowContactForm}
              data-testid="contact-info-edit-button-on-checkout"
            >
              <span> Edit </span>
            </button>
          }
        </div>
      </div>

      {!authInfo.userId && (!currentUser.email && !currentUser.first_name && !currentUser.phone) &&
        <div className="d-flex justify-content-center justify-content-md-start mb-md-0 mb-4 col-md-6 ">
          <button type="button" className="btn btn-large outline mt-md-3 mt-0 w-100" onClick={handleShowContactForm}>
            Add contact info
          </button>
        </div>
      }

      <CustomModal
        showModal={showContactForm}
        closeModal={handleCloseContactForm}
        title={`${isDesignV2 ? 'Edit Contact Details' : 'Add Contact Info'}`}
        ContactmodalWraperClass={isDesignV2 ? 'conntact-info-checkout-mobile-designv2' : ''}

      >
        <LoyaltyContactInfoForm
          closeModal={handleCloseContactForm}
          currentUserInfo={loyaltyUserInfo}
          knownEmail={knownEmail}
          knownPhone={knownPhone}
        />
      </ CustomModal>

    </>
  );
};

export default ContactMethod;
